import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "./AuthContext";
import { initializeTheme, watchSystemTheme, applyTheme } from "../utils/theme";
import { devLog, logError } from "../utils/logger";

/**
 * SettingsContext
 *
 * Manages user settings globally across the application with localStorage caching.
 *
 * Features:
 * - Fast initial load from localStorage cache
 * - Background sync to backend
 * - Automatic fallback to cache if backend unavailable
 * - Real-time settings updates across all components
 *
 * Usage:
 * - Wrap your app with <SettingsProvider>
 * - Use useSettings() hook in components that need settings
 */

const SETTINGS_CACHE_KEY = "traces:settings:cache";

const SettingsContext = createContext(null);

export function SettingsProvider({ children }) {
  const { user, updateUser } = useAuth();
  const location = useLocation();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Track if we're currently saving to prevent race conditions
  const isSaving = useRef(false);

  // Debug: Log when SettingsProvider renders
  devLog(
    "SettingsProvider render, user:",
    user?.id,
    "settings:",
    settings,
    "loading:",
    loading,
  );

  // Public routes that should always use light mode
  const publicRoutes = ["/", "/login", "/signup", "/forgot-password"];
  const isPublicRoute =
    publicRoutes.includes(location.pathname) ||
    location.pathname.startsWith("/reset-password/");

  /**
   * Load settings from localStorage cache
   */
  const loadFromCache = useCallback(() => {
    try {
      const cached = localStorage.getItem(SETTINGS_CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        devLog("SettingsContext: Loaded from cache:", parsed);
        return parsed;
      }
    } catch (err) {
      logError("SettingsContext: Failed to load from cache:", err);
    }
    return null;
  }, []);

  /**
   * Save settings to localStorage cache
   */
  const saveToCache = useCallback((settingsData) => {
    try {
      localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(settingsData));
      devLog("SettingsContext: Saved to cache");
    } catch (err) {
      logError("SettingsContext: Failed to save to cache:", err);
    }
  }, []);

  /**
   * Fetch settings from backend
   */
  const fetchSettings = useCallback(async () => {
    try {
      devLog("SettingsContext: Starting fetch from backend");
      setError(null);

      // Try to fetch from backend
      devLog("SettingsContext: Calling api.getUserSettings()");
      const response = await api.getUserSettings();
      const fetchedSettings = response.data;

      devLog("SettingsContext: Fetched from backend:", fetchedSettings);

      // Update state and cache
      setSettings(fetchedSettings);
      saveToCache(fetchedSettings);
    } catch (err) {
      setError(err.message);
      logError("SettingsContext: Failed to fetch from backend:", err);

      // Fallback to cache if backend fails
      const cached = loadFromCache();
      if (cached) {
        devLog("SettingsContext: Using cache fallback");
        setSettings(cached);
      }
    } finally {
      devLog("SettingsContext: Fetch completed, setting loading to false");
      setLoading(false);
    }
  }, [saveToCache, loadFromCache]);

  /**
   * Initialize settings on mount
   * Load from cache immediately for fast UI, then sync with backend
   * Re-runs whenever user becomes available
   */
  useEffect(() => {
    devLog(
      "SettingsContext: useEffect ran, user:",
      user,
      "user?.id:",
      user?.id,
    );

    if (!user?.id) {
      devLog(
        "SettingsContext: No user available yet, will retry when user is set",
      );
      // Don't set loading = false here - wait until user is available
      return;
    }

    devLog("SettingsContext: Initializing settings for user:", user?.id);

    // Load from cache immediately for instant UI
    const cached = loadFromCache();
    if (cached) {
      devLog("SettingsContext: Found cached settings");
      setSettings(cached);
      setLoading(false);
    } else {
      devLog("SettingsContext: No cached settings, will fetch from backend");
      // Keep loading=true, fetchSettings will set it to false when done
    }

    // Then fetch from backend in background to get latest
    devLog("SettingsContext: Calling fetchSettings");
    fetchSettings();
  }, [user?.id, fetchSettings]);

  /**
   * Apply theme whenever settings.theme changes
   * Only apply theme for authenticated users on protected routes
   * Public routes always use light mode
   */
  useEffect(() => {
    // Force light mode on public routes (landing, login, signup)
    if (isPublicRoute) {
      document.documentElement.classList.remove("dark");
      return;
    }

    // Only apply theme for authenticated users on protected routes
    if (!user?.id) {
      // Force light mode for unauthenticated users
      document.documentElement.classList.remove("dark");
      return;
    }

    if (!settings?.theme) return;

    initializeTheme(settings);

    // Watch for system theme changes if in auto mode
    if (settings.theme === "auto") {
      const cleanup = watchSystemTheme((systemTheme) => {
        devLog("System theme changed to:", systemTheme);
        applyTheme(systemTheme);
      });

      return cleanup;
    }
  }, [settings?.theme, user?.id, isPublicRoute]);

  /**
   * Update a single setting
   */
  const updateSetting = useCallback(
    async (key, value) => {
      if (!user?.id) {
        throw new Error("User not authenticated");
      }

      // Optimistic update
      const updates = { [key]: value };
      setSettings((prev) => ({ ...prev, ...updates }));

      // Save to cache immediately
      const newSettings = { ...settings, ...updates };
      saveToCache(newSettings);

      // Sync to backend
      try {
        isSaving.current = true;
        setSaving(true);
        const response = await api.updateUserSettings(updates);

        // Update with server response
        setSettings(response.data);
        saveToCache(response.data);

        devLog("SettingsContext: Setting updated:", key, value);
      } catch (err) {
        logError("SettingsContext: Failed to sync setting to backend:", err);
        // Keep optimistic update even if backend fails
        // The next fetch will sync any discrepancies
      } finally {
        isSaving.current = false;
        setSaving(false);
      }
    },
    [user, settings, saveToCache],
  );

  /**
   * Update multiple settings at once
   */
  const updateSettings = useCallback(
    async (updates) => {
      if (!user?.id) {
        throw new Error("User not authenticated");
      }

      // Optimistic update
      setSettings((prev) => ({ ...prev, ...updates }));

      // Save to cache immediately
      const newSettings = { ...settings, ...updates };
      saveToCache(newSettings);

      // Sync to backend
      try {
        isSaving.current = true;
        setSaving(true);
        const response = await api.updateUserSettings(updates);

        // Update with server response
        setSettings(response.data);
        saveToCache(response.data);

// Clear session override for showRadiusCircle when settings are saved
        // This ensures the new setting becomes the source of truth
        if (updates.showRadiusCircle !== undefined) {
          sessionStorage.removeItem("traces_show_radius_circle_session");
        }

        devLog("SettingsContext: Settings updated:", updates);

        // Also update user in AuthContext if display name changed
        if (updates.displayName) {
          // Split displat name into firstName and lastName for backend
          const nameParts = updates.displayName.trim().split(/\s+/);
          const firstName = nameParts[0] || "";
          const lastName = nameParts.slice(1).join(" ") || "";

          await updateUser({ firstName, lastName });
        }

        return response.data;
      } catch (err) {
        logError("SettingsContext: Failed to sync settings to backend:", err);
        // Keep optimistic update even if backend fails
        throw err;
      } finally {
        isSaving.current = false;
        setSaving(false);
      }
    },
    [user, settings, saveToCache, updateUser],
  );

  /**
   * Change user password
   */
  const changePassword = useCallback(
    async (passwords) => {
      if (!user?.id) {
        throw new Error("User not authenticated");
      }

      try {
        setSaving(true);
        setError(null);

        devLog("SettingsContext: Changing password");
        await api.changePassword(passwords);
      } catch (err) {
        setError(err.message);
        logError("SettingsContext: Failed to change password:", err);
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [user],
  );

  /**
   * Delete user account
   */
  const deleteAccount = useCallback(
    async (data) => {
      if (!user?.id) {
        throw new Error("User not authenticated");
      }

      try {
        setSaving(true);
        setError(null);

        devLog("SettingsContext: Deleting account");
        await api.deleteAccount(data);
      } catch (err) {
        setError(err.message);
        logError("SettingsContext: Failed to delete account:", err);
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [user],
  );

  const value = {
    settings,
    loading,
    saving,
    error,
    updateSetting, // (key, value) => update single setting
    updateSettings, // (updates) => bulk update
    changePassword, // (passwords) => change user password
    deleteAccount, // (data) => delete user account
    refetch: fetchSettings, // Manually refetch from backend
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

/**
 * Hook to access settings context
 *
 * @returns {Object} { settings, loading, saving, error, updateSetting, updateSettings, changePassword, deleteAccount, refetch }
 *
 * @example
 * const { settings, updateSetting, updateSettings, changePassword, deleteAccount } = useSettings();
 *
 * // Read a setting
 * const theme = settings?.theme || 'auto';
 *
 * // Update a single setting
 * await updateSetting('theme', 'dark');
 *
 * // Update multiple settings
 * await updateSettings({ theme: 'dark', units: 'km' });
 *
 * // Change password
 * await changePassword({ currentPassword: 'old', newPassword: 'new' });
 *
 * // Delete account
 * await deleteAccount({ password: 'pass', reason: 'Not using anymore' });
 */
export function useSettings() {
  const context = useContext(SettingsContext);

  if (!context) {
    throw new Error(
      "useSettings must be used within a SettingsProvider. " +
        "Make sure to wrap your app with <SettingsProvider>.",
    );
  }

  return context;
}
